import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';

const normalize = (v) => {
  if (v === undefined || v === null) return '';
  return String(v);
};

const normalizeDecision = (v) => {
  if (v === 'Match' || v === 'Mismatch') return v;
  return '';
};

const buildInitialMap = (tracesForCase) => {
  const map = {};
  tracesForCase.forEach((t) => {
    map[String(t.id)] = {
      decision: normalizeDecision(t.decision),
      name: normalize(t.name),
      identityNumber: normalize(t.identityNumber),
    };
  });
  return map;
};

const hasAnyChange = (initial, current) => {
  const ids = Object.keys(current);
  for (const id of ids) {
    const a = initial[id] || { decision: '', name: '', identityNumber: '' };
    const b = current[id] || { decision: '', name: '', identityNumber: '' };
    if (a.decision !== b.decision || a.name !== b.name || a.identityNumber !== b.identityNumber) {
      return true;
    }
  }
  return false;
};

export default function TraceResultsModal({ caseId, isOpen, onClose }) {
  const toast = useToast();
  const { traces, loadTracesByCase, loadingTraces, updateTrace, canEditTrace } = useApp();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const modalBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const headerMutedText = useColorModeValue('gray.600', 'gray.300');
  const bodyMutedText = useColorModeValue('gray.600', 'gray.300');
  const subtleText = useColorModeValue('gray.500', 'gray.400');
  const tableHeadBg = useColorModeValue('gray.50', 'gray.700');
  const tableBorderColor = useColorModeValue('gray.100', 'gray.600');

  const tracesForCase = useMemo(() => {
    const arr = traces.filter((t) => String(t.caseId) === String(caseId));
    // Stable, readable ordering: by traceNumber then by id
    return arr.sort((a, b) => {
      const an = a.traceNumber || '';
      const bn = b.traceNumber || '';
      if (an < bn) return -1;
      if (an > bn) return 1;
      return Number(a.id) - Number(b.id);
    });
  }, [traces, caseId]);

  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 });
  const initialRef = useRef({});
  const initializedRef = useRef(false);

  // Load traces for this case when modal opens
  useEffect(() => {
    if (!isOpen || !caseId) return;
    loadTracesByCase(caseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, caseId]);

  // Initialize form values when opening (or when traces arrive the first time)
  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      setValues({});
      setSaving(false);
      setSaveProgress({ done: 0, total: 0 });
      return;
    }

    if (!initializedRef.current && tracesForCase.length > 0) {
      const init = buildInitialMap(tracesForCase);
      initialRef.current = init;
      setValues(init);
      initializedRef.current = true;
    }
  }, [isOpen, tracesForCase]);

  const dirty = useMemo(() => hasAnyChange(initialRef.current || {}, values || {}), [values]);

  const onChangeCell = (traceId, patch) => {
    setValues((prev) => {
      const id = String(traceId);
      const current = prev?.[id] || { decision: '', name: '', identityNumber: '' };
      return {
        ...(prev || {}),
        [id]: { ...current, ...patch },
      };
    });
  };

  const buildPayload = (row) => {
    // Allow clearing values: send null for empty
    const decision = normalizeDecision(row.decision);
    const name = row.name.trim();
    const identityNumber = row.identityNumber.trim();
    return {
      decision: decision ? decision : null,
      name: name ? name : null,
      identityNumber: identityNumber ? identityNumber : null,
    };
  };

  const onSave = async () => {
    if (!canEditTrace()) {
      toast({
        title: 'غير مسموح',
        description: 'لا تملك صلاحية تعديل نتائج الآثار',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (saving) return;

    const initial = initialRef.current || {};
    const current = values || {};
    const changedIds = Object.keys(current).filter((id) => {
      const a = initial[id] || { decision: '', name: '', identityNumber: '' };
      const b = current[id] || { decision: '', name: '', identityNumber: '' };
      return a.decision !== b.decision || a.name !== b.name || a.identityNumber !== b.identityNumber;
    });

    if (changedIds.length === 0) {
      toast({
        title: 'لا توجد تغييرات',
        description: 'لم يتم تعديل أي صف',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    setSaveProgress({ done: 0, total: changedIds.length });

    let ok = 0;
    let failed = 0;

    // Safer than Promise.all for large cases: update sequentially
    for (let i = 0; i < changedIds.length; i++) {
      const id = changedIds[i];
      const payload = buildPayload(current[id]);
      try {
        await updateTrace(id, payload);
        ok += 1;
      } catch (e) {
        failed += 1;
      } finally {
        setSaveProgress({ done: i + 1, total: changedIds.length });
      }
    }

    setSaving(false);

    toast({
      title: 'تم الحفظ',
      description: failed === 0
        ? `تم تحديث ${ok} أثر`
        : `تم تحديث ${ok} أثر، وفشل ${failed} أثر`,
      status: failed === 0 ? 'success' : 'warning',
      duration: 5000,
      isClosable: true,
    });

    if (failed === 0) {
      onClose();
    }
  };

  const headerRight = (
    <Flex align="center" gap={3} flexWrap="wrap">
      <Badge colorScheme="gray">{tracesForCase.length} أثر</Badge>
      {saving && (
        <Text fontSize="sm" color="gray.600">
          {saveProgress.done}/{saveProgress.total}
        </Text>
      )}
    </Flex>
  );

  const showLoading = isOpen && (loadingTraces && tracesForCase.length === 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg={modalBg} borderColor={borderColor}>
        <ModalHeader pe="56px">
          <Flex align="center" justify="space-between" gap={3}>
            <Text>تسجيل نتائج الآثار</Text>
            {headerRight}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {showLoading ? (
            <Flex align="center" justify="center" py={10}>
              <Spinner />
              <Text ml={3} color={headerMutedText}>جاري تحميل الآثار...</Text>
            </Flex>
          ) : tracesForCase.length === 0 ? (
            <Box py={6}>
              <Text color={bodyMutedText}>لا توجد آثار لهذه القضية.</Text>
            </Box>
          ) : isMobile ? (
            <VStack align="stretch" spacing={3}>
              {tracesForCase.map((trace) => {
                const row = values?.[String(trace.id)] || {
                  decision: '',
                  name: '',
                  identityNumber: '',
                };

                return (
                  <Card key={trace.id} variant="outline" borderColor={tableBorderColor} bg={modalBg}>
                    <CardBody>
                      <Flex align="center" justify="space-between" gap={3} mb={3}>
                        <Text fontWeight="bold" dir="ltr">
                          {trace.traceNumber}
                        </Text>
                        <Badge colorScheme="gray" fontSize="xs">
                          أثر
                        </Badge>
                      </Flex>

                      <VStack align="stretch" spacing={3}>
                        <FormControl>
                          <FormLabel fontSize="sm" mb={1} color={subtleText}>
                            القرار
                          </FormLabel>
                          <Select
                            size="sm"
                            value={row.decision}
                            onChange={(e) => onChangeCell(trace.id, { decision: e.target.value })}
                            isDisabled={saving || !canEditTrace()}
                            placeholder="—"
                          >
                            <option value="Match">تطابق</option>
                            <option value="Mismatch">عدم تطابق</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" mb={1} color={subtleText}>
                            الاسم
                          </FormLabel>
                          <Input
                            size="sm"
                            value={row.name}
                            onChange={(e) => onChangeCell(trace.id, { name: e.target.value })}
                            isDisabled={saving || !canEditTrace()}
                            placeholder="—"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" mb={1} color={subtleText}>
                            رقم الهوية
                          </FormLabel>
                          <Input
                            size="sm"
                            value={row.identityNumber}
                            onChange={(e) => onChangeCell(trace.id, { identityNumber: e.target.value })}
                            isDisabled={saving || !canEditTrace()}
                            placeholder="—"
                            dir="ltr"
                          />
                        </FormControl>
                      </VStack>
                    </CardBody>
                  </Card>
                );
              })}
            </VStack>
          ) : (
            <TableContainer maxH="60vh" overflowY="auto" border="1px solid" borderColor={tableBorderColor} borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} zIndex={1} bg={tableHeadBg}>
                  <Tr>
                    <Th>رقم الأثر</Th>
                    <Th>القرار</Th>
                    <Th>الاسم</Th>
                    <Th>رقم الهوية</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tracesForCase.map((trace) => {
                    const row = values?.[String(trace.id)] || {
                      decision: '',
                      name: '',
                      identityNumber: '',
                    };

                    return (
                      <Tr key={trace.id}>
                        <Td dir="ltr" fontWeight="semibold">
                          {trace.traceNumber}
                        </Td>
                        <Td>
                          <FormControl>
                            <Select
                              size="sm"
                              value={row.decision}
                              onChange={(e) => onChangeCell(trace.id, { decision: e.target.value })}
                              isDisabled={saving || !canEditTrace()}
                              placeholder="—"
                            >
                              <option value="Match">تطابق</option>
                              <option value="Mismatch">عدم تطابق</option>
                            </Select>
                          </FormControl>
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            value={row.name}
                            onChange={(e) => onChangeCell(trace.id, { name: e.target.value })}
                            isDisabled={saving || !canEditTrace()}
                            placeholder="—"
                          />
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            value={row.identityNumber}
                            onChange={(e) => onChangeCell(trace.id, { identityNumber: e.target.value })}
                            isDisabled={saving || !canEditTrace()}
                            placeholder="—"
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex w="full" align="center" justify="space-between" gap={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={saving}>
              إغلاق
            </Button>
            <Flex align="center" gap={3}>
              {dirty && (
                <Text fontSize="sm" color="orange.600">
                  توجد تغييرات غير محفوظة
                </Text>
              )}
              <Button
                colorScheme="primary"
                onClick={onSave}
                isLoading={saving}
                isDisabled={!dirty || saving || !canEditTrace()}
              >
                حفظ
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}


